import React from "react";
import api from "../lib/api";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { Monitor, WhatsappLogo, ArrowSquareOut, SpeakerHigh, DoorOpen, ListNumbers } from "@phosphor-icons/react";

export const PARTNER = {
  name: "Seu Chamador",
  tagline: "Painel de chamada de pacientes na TV da clínica",
  description: "Chame o paciente pelo nome na TV da recepção e indique a sala de atendimento, com aviso sonoro. Recepção organizada, sem gritar nome no corredor. Feito pelo mesmo time do O Seu Precificador.",
  whatsapp: "5511986879655",
  demo_url: "https://painel-clinica-1.preview.emergentagent.com/tv/99792d74-68fc-4c89-9109-2580c40c1f79",
  url: "https://oseuchamador.com",
  signup_url: "https://oseuchamador.com/login?signup=1",
  code: "PARCEIRO", discount: 30,
  features: [
    { icon: Monitor, title: "Chamada na TV", text: "Nome do paciente em destaque na TV da recepção, com o histórico das últimas chamadas." },
    { icon: DoorOpen, title: "Direcionamento à sala", text: "Mostra para qual sala / consultório o paciente deve ir. Fim da confusão no corredor." },
    { icon: SpeakerHigh, title: "Aviso sonoro", text: "Toque de chamada e leitura em voz alta do nome, para ninguém perder a vez." },
    { icon: ListNumbers, title: "Fila por profissional", text: "Cada dentista chama seus pacientes do próprio celular ou computador." },
  ],
};

export const partnerWhatsappLink = (text) => `https://wa.me/${PARTNER.whatsapp}?text=${encodeURIComponent(text)}`;
const DEMO_MSG = `Olá! Uso o O Seu Precificador e vi a demonstração do ${PARTNER.name} (painel de chamada na TV) e quero saber como ter na minha clínica.`;

export function PartnerCard() {
  const [info, setInfo] = React.useState(null);
  React.useEffect(() => { api.get("/billing/partner").then((r) => setInfo(r.data)).catch(() => {}); }, []);
  return (
    <div className="relative overflow-hidden rounded-xl border border-[#E3D3AE] bg-gradient-to-br from-[#FBF6EA] via-white to-[#F5ECD6] p-6" data-testid="partner-card">
      <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-[#C5A059]/25 blur-2xl" />
      <div className="relative grid lg:grid-cols-[auto_1fr_auto] gap-5 items-center">
        <div className="w-14 h-14 rounded-2xl bg-[#C5A059] text-white flex items-center justify-center shrink-0 shadow-lg shadow-[#C5A059]/40"><Monitor size={28} weight="duotone" /></div>
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-[#8F6E2E]">Do mesmo time do O Seu Precificador</div>
          <h3 className="font-heading font-extrabold text-xl text-slate-900 mt-0.5">{PARTNER.name} — {PARTNER.tagline}</h3>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">{PARTNER.description}</p>
          <ul className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs text-slate-700">
            {PARTNER.features.map((f) => <li key={f.title} className="flex items-center gap-1.5"><f.icon size={14} weight="duotone" className="text-[#C5A059]" />{f.title}</li>)}
          </ul>
          {info && (info.unlocked ? (
            <div className="mt-3 inline-flex items-center gap-2 rounded-md bg-white border border-[#E3D3AE] px-3 py-1.5 text-sm" data-testid="partner-code">
              Seu cupom de <strong>{info.discount_pct}% no Seu Chamador</strong>: <code className="font-mono-nums font-bold text-[#8F6E2E]">{info.code_for_chamador}</code>
            </div>
          ) : (
            <p className="mt-3 text-xs text-[#7A5B20]" data-testid="partner-locked"><Link to="/app/billing" className="underline font-semibold">Assine um plano</Link> e ganhe <strong>{info.discount_pct}% de desconto</strong> no Seu Chamador.</p>
          ))}
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          <Button asChild className="bg-[#C5A059] hover:bg-[#B08E47] text-white rounded-full"><a href={PARTNER.signup_url} target="_blank" rel="noreferrer" data-testid="partner-signup"><Monitor size={18} className="mr-1.5" /> Assinar o Seu Chamador <ArrowSquareOut size={14} className="ml-1.5" /></a></Button>
          <Button asChild variant="outline" className="rounded-full border-[#C5A059] text-[#7A5B20] hover:bg-[#FBF6EA]"><a href={PARTNER.demo_url} target="_blank" rel="noreferrer" data-testid="partner-demo-tv">Ver demonstração na TV</a></Button>
          <a href={partnerWhatsappLink(DEMO_MSG)} target="_blank" rel="noreferrer" className="text-xs text-center text-[#8F6E2E] hover:underline" data-testid="partner-demo-whatsapp"><WhatsappLogo size={14} className="inline mr-1" />Falar no WhatsApp</a>
        </div>
      </div>
    </div>
  );
}

export function PartnerLandingSection() {
  return (
    <section className="py-20 md:py-28 bg-white" data-testid="partner-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="rounded-3xl bg-slate-900 text-white p-8 md:p-14 grid lg:grid-cols-2 gap-10 items-center relative overflow-hidden">
          <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_80%_20%,#C5A059,transparent_45%)]" />
          <div className="relative">
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-[#E3C98A]"><Monitor size={16} weight="fill" /> Também do nosso time</span>
            <h2 className="mt-4 text-3xl md:text-4xl font-heading font-extrabold tracking-tight">Preço certo na mesa, recepção organizada na TV.</h2>
            <p className="mt-4 text-slate-300 text-base md:text-lg">Conheça o <strong className="text-white">{PARTNER.name}</strong>: {PARTNER.description}</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button asChild size="lg" className="rounded-full bg-[#C5A059] hover:bg-[#D4B46E] text-slate-900 font-semibold h-12 px-7"><a href={PARTNER.url} target="_blank" rel="noreferrer" data-testid="landing-partner-site"><Monitor size={18} className="mr-2" /> Conhecer o Seu Chamador</a></Button>
              <Button asChild size="lg" variant="outline" className="rounded-full h-12 px-7 border-slate-600 text-white hover:bg-slate-800"><a href={PARTNER.demo_url} target="_blank" rel="noreferrer" data-testid="landing-partner-demo">Ver demonstração na TV</a></Button>
            </div>
            <p className="mt-4 text-xs text-slate-400">Assinantes do O Seu Precificador ganham <strong className="text-[#E3C98A]">30% de desconto</strong> no Seu Chamador (cupom PARCEIRO). Já usa o Chamador? Ganhe 30% aqui com o código <code className="text-[#E3C98A]">PRECIFICADOR</code>.</p>
          </div>
          <div className="relative grid sm:grid-cols-2 gap-4">
            {PARTNER.features.map((f) => (
              <div key={f.title} className="rounded-2xl bg-white/5 border border-white/10 p-5 backdrop-blur">
                <f.icon size={24} weight="duotone" className="text-[#E3C98A]" />
                <div className="mt-3 font-semibold">{f.title}</div>
                <div className="mt-1 text-sm text-slate-300">{f.text}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
